// frontend/src/LandingPage.js
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Container, Typography, Button, Paper, Chip, Avatar,
    Divider, Select, MenuItem, InputBase,
} from '@mui/material';
import ElderlyIcon from '@mui/icons-material/Elderly';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import PaymentsIcon from '@mui/icons-material/Payments';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import apiClient from './api';
import { brand } from './theme';
import { Reveal, DecorBlob } from './components/Reveal';

const SERVICE_ICONS = {
    'مراقبت از سالمند': ElderlyIcon,
    'مراقبت از کودک': ChildCareIcon,
    'مراقبت از بیمار': LocalHospitalIcon,
    'پرستاری و تزریقات': MedicalServicesIcon,
    'همراه بیمار در بیمارستان': EmojiPeopleIcon,
    'کمک در کارهای منزل': CleaningServicesIcon,
};

const CREDENTIALS = [
    { icon: VerifiedIcon, label: 'احراز هویت با شاهکار' },
    { icon: FactCheckIcon, label: 'استعلام سوءپیشینه' },
    { icon: PaymentsIcon, label: 'پرداخت امن با درگاه بانکی' },
];

const DIFFERENTIATORS = [
    {
        icon: PaymentsIcon,
        title: 'پرداخت امن، نه نقدیِ بی‌سند',
        body: 'تراکنش از طریق درگاه بانکی ثبت و پیگیری می‌شود — رسید و سابقه‌ی شفاف برای هر پرداخت.',
    },
    {
        icon: FactCheckIcon,
        title: 'تأیید ۴مرحله‌ای، نه یک تیک ساده',
        body: 'هر همیار پیش از فعالیت، احراز هویت با شاهکار، استعلام سوءپیشینه و مصاحبه را می‌گذراند.',
    },
    {
        icon: VisibilityIcon,
        title: 'شفافیت کامل پیش از تصمیم',
        body: 'پروفایل، امتیاز و شرایط هر همیار را پیش از پذیرفتن پیشنهادش می‌بینید — نه بعد از آن.',
    },
];

const PROCESS_STEPS = [
    {
        n: '۰۱',
        title: 'ثبت‌نام و احراز هویت',
        body: 'شماره موبایل و کد ملی همیار با سامانه‌ی شاهکار تطبیق داده می‌شود تا از تطابق هویت واقعی مطمئن شویم.',
    },
    {
        n: '۰۲',
        title: 'استعلام سوءپیشینه و بررسی مدارک',
        body: 'گواهی عدم سوءپیشینه و سوابق کاری اعلام‌شده پیش از فعال‌سازی پروفایل بررسی می‌شود.',
        tag: 'مرحله‌ی سخت‌گیرانه',
    },
    {
        n: '۰۳',
        title: 'مصاحبه و ارزیابی مهارت مراقبتی',
        body: 'مهارت، تجربه و نحوه‌ی برخورد همیار با موقعیت‌های واقعی مراقبتی پیش از تأیید نهایی سنجیده می‌شود.',
    },
    {
        n: '۰۴',
        title: 'فعال‌سازی، رتبه‌بندی مستمر',
        body: 'پس از تأیید، پروفایل عمومی می‌شود — و امتیاز هر خانواده پس از پایان هر دوره‌ی مراقبت به اعتبار همیار اضافه می‌شود.',
    },
];

const FAMILY_STEPS = [
    ['ثبت درخواست', 'با جزئیات نیاز، شیفت و شرایط سالمند یا کودک'],
    ['دریافت پیشنهاد', 'از همیاران تأییدشده‌ی در دسترس'],
    ['مقایسه‌ی پروفایل‌ها', 'بر اساس سابقه، تخصص و امتیاز'],
    ['شروع همکاری', 'و ثبت نظر شما پس از پایان دوره'],
];

const PROVIDER_STEPS = [
    ['تکمیل پروفایل تخصصی', 'و طی‌کردن فرآیند احراز هویت'],
    ['مشاهده‌ی درخواست‌های باز', 'نزدیک به محل زندگی‌تان'],
    ['ارسال پیشنهاد', 'با نرخ و شرایط پیشنهادی خودتان'],
    ['دریافت امتیاز', 'از هر خانواده و رشد اعتبار پروفایل'],
];

const SCHEDULE = [
    { d: 'ش', label: 'روز', kind: 'day' },
    { d: 'ی', label: 'روز', kind: 'day' },
    { d: 'د', label: '۲۴س', kind: 'full' },
    { d: 'س', label: '', kind: 'off' },
    { d: 'چ', label: 'شب', kind: 'night' },
    { d: 'پ', label: 'شب', kind: 'night' },
    { d: 'ج', label: '', kind: 'off' },
];

const scheduleColors = {
    day: { bg: brand.primaryTint, color: brand.primary },
    night: { bg: brand.primaryDeep, color: brand.heroText },
    full: { bg: brand.accentTint, color: brand.accentDeep },
    off: { bg: brand.paper, color: brand.inkFaint },
};


function SectionKicker({ children }) {
    return (
        <Typography
            component="span"
            sx={{ display: 'block', fontSize: '.8rem', fontWeight: 700, color: 'primary.main', letterSpacing: '.03em', mb: 1.5 }}
        >
            {children}
        </Typography>
    );
}

function LandingPage() {
    const [services, setServices] = useState([]);
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        apiClient.get('/api/services/')
            .then((res) => setServices(Array.isArray(res.data) ? res.data : []))
            .catch(() => setServices([]));
        apiClient.get('/api/articles/')
            .then((res) => setArticles(Array.isArray(res.data) ? res.data.slice(0, 3) : []))
            .catch(() => setArticles([]));
    }, []);

    const primaryServices = services.slice(0, 2);
    const secondaryServices = services.slice(2);

    return (
        <Box sx={{ bgcolor: 'background.default' }}>

            {/* ================= HERO ================= */}
            <Box
                sx={{
                    bgcolor: brand.primaryDeep,
                    color: brand.heroText,
                    pt: { xs: 6, md: 8 },
                    pb: { xs: 8, md: 12 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* گرافیک تزئینی پس‌زمینه */}
                <Box sx={{ position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)',
                    backgroundSize: '26px 26px' }} />
                <Box sx={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%',
                    background: `radial-gradient(circle, ${brand.accent}33 0%, transparent 70%)`,
                    top: -180, insetInlineEnd: -120, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%',
                    background: `radial-gradient(circle, ${brand.primaryTint}22 0%, transparent 70%)`,
                    bottom: -200, insetInlineStart: -100, pointerEvents: 'none' }} />
                <Box component="svg" viewBox="0 0 1440 90" preserveAspectRatio="none" sx={{
                    position: 'absolute', bottom: -1, insetInlineStart: 0, width: '100%', height: 60,
                    color: 'background.default', pointerEvents: 'none',
                }}>
                    <path d="M0,40 C320,90 720,0 1080,30 C1260,45 1360,40 1440,20 L1440,90 L0,90 Z" fill="currentColor" />
                </Box>

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 7, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                        <Box sx={{
                            flex: '1 1 520px', minWidth: 0, width: '100%',
                            '@keyframes heroFadeUp': { from: { opacity: 0, transform: 'translateY(18px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                            animation: 'heroFadeUp .8s cubic-bezier(.16,1,.3,1) both',
                        }}>
                            <Typography sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: '.82rem', fontWeight: 700, color: 'secondary.main', mb: 2.5 }}>
                                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'secondary.main' }} />
                                الو یارا — پلتفرم مراقبت خانگی
                            </Typography>
                            <Typography variant="h2" sx={{ fontSize: { xs: '2.15rem', md: '3.1rem' }, lineHeight: 1.28, letterSpacing: '-.01em' }}>
                                یک تماس، تا{' '}
                                <Box component="span" sx={{ color: 'secondary.main' }}>آرامشِ</Box>
                                <br />
                                خانواده‌ی شما
                            </Typography>
                            <Typography sx={{ mt: 3, maxWidth: '46ch', fontSize: '1.1rem', color: 'rgba(245,243,234,.82)' }}>
                                الو یارا شما را به مراقبان و پرستارانِ احرازهویت‌شده برای سالمندان، کودکان و بیماران در منزل
                                وصل می‌کند؛ با شیفت، نرخ و سابقه‌ی شفاف — پیش از هر تصمیمی.
                            </Typography>

                            {/* quick-start */}
                            <Paper
                                component="form"
                                onSubmit={(e) => e.preventDefault()}
                                elevation={0}
                                sx={{
                                    mt: 4, p: 0.75, maxWidth: 580,
                                    display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch',
                                    borderRadius: { xs: 4, sm: 999 },
                                    boxShadow: '0 24px 44px -20px rgba(0,0,0,.5)',
                                }}
                            >
                                <Box sx={{ flex: 1, px: 2.5, py: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '.68rem', fontWeight: 700, color: 'text.disabled' }}>نوع مراقبت</Typography>
                                    <Select
                                        variant="standard"
                                        disableUnderline
                                        defaultValue="سالمند"
                                        fullWidth
                                        sx={{ fontWeight: 700, fontSize: '.92rem' }}
                                    >
                                        <MenuItem value="سالمند">مراقبت از سالمند</MenuItem>
                                        <MenuItem value="کودک">مراقبت از کودک</MenuItem>
                                        <MenuItem value="بیمار">مراقبت از بیمار</MenuItem>
                                    </Select>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ my: 1, display: { xs: 'none', sm: 'block' } }} />
                                <Box sx={{ flex: 1, px: 2.5, py: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '.68rem', fontWeight: 700, color: 'text.disabled' }}>شهر</Typography>
                                    <InputBase placeholder="مثلاً تهران" fullWidth sx={{ fontWeight: 700, fontSize: '.92rem' }} />
                                </Box>
                                <Button type="submit" variant="contained" color="secondary" sx={{ m: { xs: 0.5, sm: 0.5 }, flexShrink: 0 }}>
                                    پیدا کردن همیار
                                </Button>
                            </Paper>
                            <Typography sx={{ mt: 2.25, fontSize: '.86rem', color: 'rgba(245,243,234,.68)' }}>
                                یا اگر می‌خواهید کار کنید،{' '}
                                <Box component={RouterLink} to="/register" sx={{ color: 'secondary.main', fontWeight: 700, textDecoration: 'none' }}>
                                    همین‌جا به‌عنوان همیار ثبت‌نام کنید
                                </Box>.
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', mt: 5.5, pt: 3, borderTop: '1px solid rgba(245,243,234,.16)' }}>
                                {CREDENTIALS.map(({ icon: Icon, label }) => (
                                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '.85rem', color: 'rgba(245,243,234,.78)' }}>
                                        <Icon sx={{ fontSize: 17, color: 'secondary.main' }} />
                                        {label}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* schedule card */}
                        <Box sx={{ flex: '1 1 400px', minWidth: 0, width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <Box>
                                <Paper sx={{
                                    width: '100%', maxWidth: 400, borderRadius: 2.5, overflow: 'hidden',
                                    boxShadow: '0 30px 60px -20px rgba(0,0,0,.5), 0 3px 0 ' + brand.accent,
                                    '@keyframes cardFadeIn': { from: { opacity: 0, transform: 'translateY(24px) rotate(0deg)' }, to: { opacity: 1, transform: 'translateY(0) rotate(-1.4deg)' } },
                                    '@keyframes cardFloat': { '0%, 100%': { transform: 'translateY(0) rotate(-1.4deg)' }, '50%': { transform: 'translateY(-8px) rotate(-1.4deg)' } },
                                    animation: 'cardFadeIn .9s .15s cubic-bezier(.16,1,.3,1) both, cardFloat 6s 1.05s ease-in-out infinite',
                                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 800 }}>ل</Avatar>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '.98rem' }}>لیلا موسوی</Typography>
                                            <Typography sx={{ fontSize: '.8rem', color: 'text.disabled' }}>همیار مراقبت سالمند</Typography>
                                        </Box>
                                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: .5, fontSize: '.85rem', fontWeight: 700 }}>
                                            <StarIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                                            ۴٫۸
                                        </Box>
                                    </Box>
                                    <Box sx={{ p: 2.25, pb: 1 }}>
                                        <Typography sx={{ fontSize: '.78rem', color: 'text.disabled', fontWeight: 600, mb: 1.5 }}>برنامه‌ی هفتگی مراقبت</Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: .75 }}>
                                            {SCHEDULE.map((s) => (
                                                <Box key={s.d} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .75 }}>
                                                    <Typography sx={{ fontSize: '.72rem', color: 'text.disabled', fontWeight: 600 }}>{s.d}</Typography>
                                                    <Box sx={{
                                                        width: '100%', height: 52, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '.66rem', fontWeight: 700,
                                                        bgcolor: scheduleColors[s.kind].bg, color: scheduleColors[s.kind].color,
                                                        border: s.kind === 'off' ? '1px dashed' : 'none', borderColor: 'divider',
                                                    }}>
                                                        {s.label}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2.25, px: 2.25, pb: 2.25, pt: 1.75, fontSize: '.72rem', color: 'text.disabled' }}>
                                        <span>شیفت روز</span><span>شیفت شب</span><span>شبانه‌روزی</span>
                                    </Box>
                                </Paper>
                                <Typography sx={{ textAlign: 'center', fontSize: '.74rem', color: 'rgba(245,243,234,.55)', mt: 2 }}>
                                    نمونه‌ی برنامه‌ی هفتگی — صرفاً جهت نمایش قابلیت پلتفرم
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* ================= WHY DIFFERENT ================= */}
            <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider', py: 7, position: 'relative', overflow: 'hidden' }}>
                <DecorBlob color={`${brand.primary}14`} size={340} top={-160} insetInlineEnd={-80} />
                <Container maxWidth="lg" sx={{ position: 'relative' }}>
                    <Reveal><SectionKicker>چرا الو یارا</SectionKicker></Reveal>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 4.5 }}>
                        {DIFFERENTIATORS.map(({ icon: Icon, title, body }, i) => (
                            <Reveal key={title} delay={i * 0.1} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Box sx={{
                                    flexShrink: 0, width: 40, height: 40, borderRadius: 1.5, bgcolor: 'primary.light', color: 'primary.main',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform .25s ease',
                                    '&:hover': { transform: 'scale(1.12) rotate(-4deg)' },
                                }}>
                                    <Icon fontSize="small" />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, mb: .5 }}>{title}</Typography>
                                    <Typography sx={{ fontSize: '.88rem', color: 'text.secondary' }}>{body}</Typography>
                                </Box>
                            </Reveal>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ================= PROCESS ================= */}
            <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
                <Reveal sx={{ maxWidth: 640, mb: 6 }}>
                    <SectionKicker>پیش از تأیید هر همیار</SectionKicker>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' } }}>
                        هیچ همیاری بدون این ۴ مرحله وارد پلتفرم نمی‌شود
                    </Typography>
                    <Typography sx={{ mt: 2, fontSize: '1.02rem', color: 'text.secondary', maxWidth: '54ch' }}>
                        چون این کار درباره‌ی سالمترین و آسیب‌پذیرترین اعضای خانواده‌ی شماست، فرآیند پذیرش همیاران
                        سخت‌گیرانه و شفاف طراحی شده.
                    </Typography>
                </Reveal>
                <Box sx={{ position: 'relative' }}>
                    <Box sx={{
                        position: 'absolute', insetInlineStart: 25, top: 0, bottom: 0, width: 2,
                        background: `linear-gradient(180deg, ${brand.primary}00, ${brand.primary}55 15%, ${brand.primary}55 85%, ${brand.primary}00)`,
                        display: { xs: 'none', sm: 'block' },
                    }} />
                    {PROCESS_STEPS.map((step, i) => (
                        <Reveal key={step.n} delay={i * 0.08} sx={{
                            display: 'flex', gap: 3.25, py: 3.75, borderTop: '1px solid', borderColor: 'divider', position: 'relative',
                            ...(i === PROCESS_STEPS.length - 1 && { borderBottom: '1px solid', borderBottomColor: 'divider' }),
                        }}>
                            <Typography sx={{
                                flexShrink: 0, width: 52, fontWeight: 900, fontSize: '1.5rem', color: 'divider',
                                bgcolor: 'background.default', zIndex: 1,
                            }}>{step.n}</Typography>
                            <Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '1.08rem' }}>{step.title}</Typography>
                                <Typography sx={{ mt: 1, color: 'text.secondary', fontSize: '.98rem', maxWidth: '60ch' }}>{step.body}</Typography>
                                {step.tag && (
                                    <Chip label={step.tag} size="small" sx={{ mt: 1.25, bgcolor: 'primary.light', color: 'primary.main', fontWeight: 700 }} />
                                )}
                            </Box>
                        </Reveal>
                    ))}
                </Box>
            </Container>

            {/* ================= SERVICES ================= */}
            <Box sx={{ bgcolor: 'background.paper', py: { xs: 7, md: 11 } }}>
                <Container maxWidth="lg">
                    <Reveal sx={{ maxWidth: 640, mb: 6 }}>
                        <SectionKicker>خدمات</SectionKicker>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' } }}>
                            هر نوع مراقبتی که خانه‌ی شما به آن نیاز دارد
                        </Typography>
                    </Reveal>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {primaryServices.map((svc, i) => {
                            const Icon = SERVICE_ICONS[svc.name] || VolunteerActivismIcon;
                            return (
                                <Reveal key={svc.id} delay={i * 0.1} sx={{
                                    p: 4, borderRadius: 3, minHeight: 190,
                                    bgcolor: i === 0 ? brand.primaryDeep : 'primary.main',
                                    color: brand.heroText, position: 'relative', overflow: 'hidden',
                                    display: 'flex', flexDirection: 'column', gap: 1.75, cursor: 'default',
                                    transition: 'transform .25s ease, box-shadow .25s ease',
                                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 40px -12px rgba(0,0,0,.4)' },
                                }}>
                                    <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)', top: -50, insetInlineEnd: -40 }} />
                                    <Icon sx={{ fontSize: 34, position: 'relative' }} />
                                    <Typography variant="h5" sx={{ fontSize: '1.28rem', position: 'relative' }}>{svc.name}</Typography>
                                    <Typography sx={{ color: 'rgba(245,243,234,.75)', fontSize: '.94rem', position: 'relative' }}>{svc.description}</Typography>
                                </Reveal>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                        {secondaryServices.map((svc, i) => (
                            <Reveal key={svc.id} delay={i * 0.06} sx={{
                                p: 2.75, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider',
                                transition: 'transform .2s ease, border-color .2s ease',
                                '&:hover': { transform: 'translateY(-3px)', borderColor: 'primary.main' },
                            }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '.98rem' }}>{svc.name}</Typography>
                                <Typography sx={{ mt: .5, fontSize: '.86rem', color: 'text.secondary' }}>{svc.description}</Typography>
                            </Reveal>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ================= PROFILE SHOWCASE ================= */}
            <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 }, position: 'relative', overflow: 'hidden' }}>
                <DecorBlob color={`${brand.accent}1c`} size={300} bottom={-100} insetInlineStart={-60} />
                <Box sx={{ display: 'flex', gap: 7, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, position: 'relative' }}>
                    <Reveal sx={{ flex: '1 1 420px' }}>
                        <SectionKicker>شفافیت پیش از تصمیم</SectionKicker>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' } }}>
                            پروفایل هر همیار را پیش از انتخاب، کامل می‌بینید
                        </Typography>
                        <Typography sx={{ mt: 2, color: 'text.secondary', fontSize: '1.02rem', maxWidth: '50ch' }}>
                            سابقه‌ی کاری، تخصص‌ها، وضعیت تأیید هویت و امتیازِ خانواده‌های قبلی — همه در یک پروفایل،
                            پیش از اینکه پیشنهادش را بپذیرید.
                        </Typography>
                    </Reveal>
                    <Reveal delay={0.15} sx={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Paper variant="outlined" sx={{
                            width: '100%', maxWidth: 360, p: 3, borderRadius: 2,
                            transition: 'transform .3s ease, box-shadow .3s ease',
                            '&:hover': { transform: 'translateY(-4px) rotate(-0.6deg)', boxShadow: '0 20px 40px -16px rgba(15,51,43,.25)' },
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 52, height: 52, bgcolor: 'primary.light', color: 'primary.main', fontWeight: 800, fontSize: '1.2rem' }}>س</Avatar>
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }}>سارا احمدی</Typography>
                                    <Chip
                                        icon={<VerifiedIcon sx={{ fontSize: '13px !important' }} />}
                                        label="هویت تأییدشده"
                                        size="small"
                                        sx={{ mt: .5, bgcolor: 'primary.light', color: 'primary.main', height: 22, fontSize: '.7rem' }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.25 }}>
                                {['مراقبت سالمند', 'آلزایمر', 'شبانه‌روزی'].map((c) => (
                                    <Chip key={c} label={c} size="small" variant="outlined" sx={{ fontSize: '.76rem' }} />
                                ))}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, mt: 2.5, pt: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
                                {[['۴٫۹', 'امتیاز'], ['۶', 'سال سابقه'], ['۳۲', 'دوره‌ی مراقبت']].map(([v, l]) => (
                                    <Box key={l}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '1.08rem' }}>{v}</Typography>
                                        <Typography sx={{ fontSize: '.72rem', color: 'text.disabled' }}>{l}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                        <Typography sx={{ fontSize: '.76rem', color: 'text.disabled', mt: 1.75 }}>
                            نمونه‌ی پروفایل همیار — صرفاً جهت نمایش
                        </Typography>
                    </Reveal>
                </Box>
            </Container>

            {/* ================= TWO PATHS ================= */}
            <Box sx={{ py: { xs: 7, md: 11 } }}>
                <Container maxWidth="lg">
                    <Reveal sx={{ maxWidth: 640, mb: 6 }}>
                        <SectionKicker>چطور کار می‌کند</SectionKicker>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' } }}>
                            یک مسیر برای خانواده‌ها، یک مسیر برای همیاران
                        </Typography>
                    </Reveal>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
                        {[
                            { kicker: 'برای خانواده‌ها', title: 'مراقب مناسب را خودتان انتخاب می‌کنید', steps: FAMILY_STEPS, to: '/create-request', label: 'ثبت درخواست مراقبت', variant: 'contained', icon: FamilyRestroomIcon },
                            { kicker: 'برای همیاران', title: 'کار پیدا کنید، اعتبار بسازید', steps: PROVIDER_STEPS, to: '/register', label: 'ثبت‌نام به‌عنوان همیار', variant: 'outlined', icon: VolunteerActivismIcon },
                        ].map((col, i) => (
                            <Reveal key={col.kicker} delay={i * 0.12} sx={{
                                p: { xs: 3.5, md: 4.5 }, bgcolor: 'background.paper',
                                transition: 'background-color .2s ease',
                                '&:hover': { bgcolor: brand.paper },
                                ...(i === 1 && { borderInlineStart: { md: '1px solid' }, borderTop: { xs: '1px solid', md: 'none' }, borderColor: 'divider' }),
                            }}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '50%', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: i === 0 ? brand.primaryTint : brand.accentTint, color: i === 0 ? brand.primary : brand.accentDeep,
                                }}>
                                    <col.icon fontSize="small" />
                                </Box>
                                <SectionKicker>{col.kicker}</SectionKicker>
                                <Typography variant="h5" sx={{ fontSize: '1.35rem', mb: 3 }}>{col.title}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {col.steps.map(([t, d], idx) => (
                                        <Box key={t} sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
                                            <Box sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', bgcolor: 'primary.light', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.74rem', fontWeight: 800, mt: '2px' }}>
                                                {idx + 1}
                                            </Box>
                                            <Typography sx={{ fontSize: '.93rem', color: 'text.secondary' }}>
                                                <Box component="b" sx={{ color: 'text.primary', fontWeight: 700 }}>{t}</Box> {d}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Button component={RouterLink} to={col.to} variant={col.variant} size="small" sx={{ mt: 3.25 }}>
                                    {col.label}
                                </Button>
                            </Reveal>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* ================= ARTICLES ================= */}
            {articles.length > 0 && (
                <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', py: { xs: 7, md: 10 } }}>
                    <Container maxWidth="lg">
                        <Reveal sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <SectionKicker>مقالات</SectionKicker>
                                <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '1.9rem' } }}>
                                    راهنمای خانواده برای مراقبت در منزل
                                </Typography>
                            </Box>
                            <Button component={RouterLink} to="/blog" variant="outlined" size="small">همه‌ی مقالات</Button>
                        </Reveal>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 2.5 }}>
                            {articles.map((a, i) => (
                                <Reveal key={a.id} delay={i * 0.08}>
                                    <Paper
                                        component={RouterLink} to={`/blog/${a.slug}`} variant="outlined"
                                        sx={{
                                            p: 2.75, textDecoration: 'none', color: 'inherit', display: 'block', height: '100%',
                                            transition: 'transform .2s ease, border-color .2s ease, box-shadow .2s ease',
                                            '&:hover': { borderColor: 'primary.main', transform: 'translateY(-3px)', boxShadow: '0 14px 30px -14px rgba(15,51,43,.3)' },
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>{a.title}</Typography>
                                        <Typography sx={{ fontSize: '.88rem', color: 'text.secondary' }}>{a.excerpt}</Typography>
                                    </Paper>
                                </Reveal>
                            ))}
                        </Box>
                    </Container>
                </Box>
            )}

            {/* ================= MANIFESTO ================= */}
            <Box sx={{ bgcolor: 'primary.light', py: 9, position: 'relative', overflow: 'hidden' }}>
                <Typography component="span" aria-hidden sx={{
                    position: 'absolute', top: -30, insetInlineStart: { xs: 8, md: 40 },
                    fontFamily: brand.fontQuote, fontSize: '9rem', color: brand.primary, opacity: 0.12, lineHeight: 1, userSelect: 'none',
                }}>
                    «
                </Typography>
                <Container maxWidth="md" sx={{ position: 'relative' }}>
                    <Reveal>
                        <Typography sx={{ fontFamily: brand.fontQuote, fontWeight: 600, fontSize: { xs: '1.35rem', md: '1.7rem' }, lineHeight: 1.85, color: 'primary.dark', textAlign: 'center' }}>
                            «اعتماد را نمی‌فروشیم؛ می‌سازیمش — یک احراز هویت، یک استعلام،
                            <br />
                            یک مصاحبه و یک دوره‌ی مراقبت در یک زمان.»
                        </Typography>
                        <Typography sx={{ mt: 3, fontWeight: 700, fontSize: '.85rem', color: 'text.secondary', textAlign: 'center' }}>
                            — تیم الو یارا
                        </Typography>
                    </Reveal>
                </Container>
            </Box>

            {/* ================= FINAL CTA ================= */}
            <Box sx={{ bgcolor: brand.primaryDeep, color: brand.heroText, py: 9, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0, opacity: 0.4, pointerEvents: 'none',
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
                    backgroundSize: '26px 26px' }} />
                <DecorBlob color={`${brand.accent}30`} size={420} top={-180} insetInlineEnd={-140} />
                <DecorBlob color={`${brand.primaryTint}22`} size={340} bottom={-160} insetInlineStart={-100} />
                <Container maxWidth="md" sx={{ position: 'relative' }}>
                    <Reveal>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.7rem', md: '2.1rem' }, maxWidth: '20ch', mx: 'auto' }}>
                            همین امروز، برای عزیزانتان شروع کنید
                        </Typography>
                        <Typography sx={{ mt: 2, color: 'rgba(245,243,234,.75)', fontSize: '1.02rem' }}>
                            ثبت درخواست رایگان است — پیشنهاد همیاران را ببینید، بعد تصمیم بگیرید.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.75, mt: 4, flexWrap: 'wrap' }}>
                            <Button component={RouterLink} to="/create-request" variant="contained" color="secondary" size="large">
                                ثبت درخواست مراقبت
                            </Button>
                            <Button
                                component={RouterLink} to="/register" variant="outlined" size="large"
                                sx={{ color: brand.heroText, borderColor: 'rgba(245,243,234,.32)', '&:hover': { borderColor: 'rgba(245,243,234,.65)' } }}
                            >
                                همیار می‌شوم
                            </Button>
                        </Box>
                    </Reveal>
                </Container>
            </Box>

            {/* ================= FOOTER ================= */}
            <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider', py: 6 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                        <Typography sx={{ fontWeight: 800 }}>الو یارا</Typography>
                        <Box sx={{ display: 'flex', gap: 3.5, flexWrap: 'wrap', fontSize: '.88rem', color: 'text.secondary' }}>
                            <Box component={RouterLink} to="/blog" sx={{ color: 'inherit', textDecoration: 'none' }}>مقالات</Box>
                            <Box component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none' }}>درباره ما</Box>
                            <Box component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none' }}>تماس با ما</Box>
                            <Box component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none' }}>قوانین و مقررات</Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', fontSize: '.8rem', color: 'text.disabled' }}>
                        <span>© ۱۴۰۴ — تمام حقوق برای پلتفرم الو یارا محفوظ است.</span>
                        <span>تهران، ایران</span>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

export default LandingPage;
